import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { guardiaOspite } from '@/services/guardiaOspite';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import type { BoardSource, BoardView, CaptureBoardRequest, PositionAnalysisView, PositionResponse } from '@/types/myBoards';
import type { BoardResponse, BoardsPage } from '@/types/myBoards';
import type {
  QgammonLeaderboardResponse,
  QgammonLiveResponse,
  QgammonMatchSummary,
  QgammonMeResponse,
  QgammonMyMatchesArgs,
  QgammonMyMatchesResponse,
  QgammonReplay,
  QgammonEloHistoryResponse,
  QgammonStatsResponse,
} from '@/types/qgammon';

/**
 * QGammon API client. Talks to qgammon-api (REST) — leaderboard + match
 * history for the `/backgammon` surface. Live play (Socket.IO to the
 * qgammon-gameserver) lands in Fase 6 part 2 with the match board.
 *
 * Auth: a resource-scoped Logto Bearer (audience `https://qgammon.qlash.gg/api`)
 * minted by getQgammonToken(), with a dev-bypass fallback (`X-Dev-Bypass` +
 * optional `X-Dev-User`) for local testing against the MacMini before a user is
 * signed in. The resolution order (Logto → dev-bypass → none) is centralised in
 * `@/lib/qgammon/auth` (qgammonAuthHeaders) and shared with every gameserver
 * touchpoint — the per-user Logto identity is the fix for the shared "Dev"
 * dev-bypass user that made two humans collide on one userId.
 *
 * Dev: env.qgammonApiUrl = '/qgammon-api', Vite proxies to the OVH PROD API
 * (`https://qgammon.qlash.gg`) by default — override VITE_QGAMMON_API_URL for
 * a local backend. Prod: 'https://qgammon.qlash.gg'.
 */
/**
 * Un endpoint che NON si ritenta da solo: `extraOptions: NESSUN_RITENTATIVO`. ⚠️ `maxRetries: 0` da solo NON basta: il
 * `retryCondition` del client (sotto) ha la sua regola e scavalca quella di default che legge `maxRetries` — misurato in
 * `tests/my-boards-endpoint.test.tsx`: una POST su un 500 partiva due volte. Per questo il predicato del client LEGGE
 * `maxRetries` e dice no a 0 (RTK non lascia sostituire il predicato per endpoint: il tipo lo vieta).
 */
const NESSUN_RITENTATIVO = { maxRetries: 0 } as const;

export const qgammonApi = createApi({
  reducerPath: 'qgammonApi',
  baseQuery: guardiaOspite('qgammon', retry(
    fetchBaseQuery({
      baseUrl: env.qgammonApiUrl,
      prepareHeaders: async (headers, { endpoint }) => {
        headers.set('Accept', 'application/json');
        // `GET /matches/live` e' PUBBLICA (ADR-0058): nessun token, e nessuna attesa sulla catena dei
        // token per una domanda che non ne ha bisogno. E nemmeno `x-platform-id`: un'intestazione non
        // semplice forza un preflight CORS, e il gameserver non manda `Access-Control-Max-Age` ⇒ due
        // richieste ogni 15 s invece di una (seguito della revisione di #437).
        // E `GET /positions/:id` di My Boards: pubblica finche' la partita d'origine si puo' leggere (ADR-0191 §3).
        if (endpoint === 'getLiveMatches' || endpoint === 'getPosition') return headers;
        headers.set('x-platform-id', env.platformId);
        // Logto Bearer if signed in, else dev-bypass headers (dev only).
        const auth = await qgammonAuthHeaders();
        for (const [k, v] of Object.entries(auth)) headers.set(k, v);
        return headers;
      },
    }),
    {
      retryCondition: (error, _args, { attempt, extraOptions }) => {
        if ((extraOptions as { maxRetries?: number } | undefined)?.maxRetries === 0) return false;
        if (attempt > 1) return false;
        const status = (error as { status?: number | string }).status;
        if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
        return typeof status === 'number' && status >= 500;
      },
    },
  )),
  keepUnusedDataFor: 120,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: ['Leaderboard', 'Me', 'MyMatches', 'Match', 'MyStats', 'MyEloHistory', 'Boards'],
  endpoints: (builder) => ({
    /** ELO leaderboard (`GET /leaderboard`) → `{ players: [...] }`. */
    getLeaderboard: builder.query<QgammonLeaderboardResponse, void>({
      query: () => '/leaderboard',
      providesTags: ['Leaderboard'],
    }),
    /** Authenticated caller's identity (`GET /auth/me`). Requires auth/dev-bypass. */
    getMe: builder.query<QgammonMeResponse, void>({
      query: () => '/auth/me',
      providesTags: ['Me'],
      keepUnusedDataFor: 300,
    }),
    /**
     * The caller's matches (`GET /matches/mine`). Requires auth/dev-bypass. Il server ne da 20 per volta:
     * `{ cursor }` (il `nextCursor` della pagina prima) chiede la successiva. Ogni pagina e' una chiave di cache sua.
     */
    getMyMatches: builder.query<QgammonMyMatchesResponse, QgammonMyMatchesArgs | void>({
      query: (args) => (args?.cursor ? `/matches/mine?cursor=${encodeURIComponent(args.cursor)}` : '/matches/mine'),
      providesTags: ['MyMatches'],
    }),
    /** The caller's aggregate stats (`GET /me/stats`) — rank, W/L, gammons, PR. Staging only for now. */
    getMyStats: builder.query<QgammonStatsResponse, void>({
      query: () => '/me/stats',
      providesTags: ['MyStats'],
    }),
    /** The caller's ELO history (`GET /me/elo-history?limit=N`), newest first. Staging only for now. */
    getMyEloHistory: builder.query<QgammonEloHistoryResponse, number | void>({
      query: (limit) => `/me/elo-history?limit=${limit ?? 30}`,
      providesTags: ['MyEloHistory'],
    }),
    /** Full replay (`GET /matches/:id/replay`). Finished matches only (404 else). */
    getReplay: builder.query<QgammonReplay, string>({
      query: (matchId) => `/matches/${encodeURIComponent(matchId)}/replay`,
    }),
    /** A single match summary (`GET /matches/:id`) — for the replay's W/B
     * perspective (maps the viewer's id → colour). Finished matches only. */
    getMatch: builder.query<{ match: QgammonMatchSummary }, string>({
      query: (matchId) => `/matches/${encodeURIComponent(matchId)}`,
      providesTags: (_r, _e, id) => [{ type: 'Match' as const, id }],
    }),
    /**
     * Le partite in corso adesso (`GET /matches/live`). ⚠️ Vive sul GAMESERVER (`env.qgammonWsUrl`,
     * host `-ws`), non sull'api: l'URL e' assoluto, e `fetchBaseQuery` lo usa cosi' com'e'.
     * Pubblica, solo metadati (v. `QgammonLiveMatch`).
     */
    getLiveMatches: builder.query<QgammonLiveResponse, void>({
      query: () => `${env.qgammonWsUrl}/matches/live`,
    }),

    // ── My Boards (ADR-0191; QGammon `apps/qgammon-api/src/boards/routes.ts` e `apps/qgammon-gameserver/src/boards/routes.ts`) ──
    /**
     * I board del giocatore (`GET /boards?cursor&source&limit`, api): SOLO i propri, il piu' recente in cima, a CURSORE (`next` =
     * uuid del board dopo cui si riparte). L'argomento e' il filtro (`all` o una provenienza): e' lato server, perche' filtrare
     * nel client una pagina da 20 lascia buchi. Niente totale: «Tutti» e' senza numero.
     * ⚠️ Il server rifiuta (400 `validation`) un cursore che non sia un board visibile del chiamante: dopo un «Togli» la rilettura
     * di una pagina col cursore vecchio sarebbe quel 400, e l'infinite query ricalcola il cursore dalla pagina appena riletta.
     */
    getBoards: builder.infiniteQuery<BoardsPage, BoardSource | 'all', string | null>({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (ultima) => ultima.next ?? undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: '/boards',
        params: {
          ...(queryArg !== 'all' && { source: queryArg }),
          ...(pageParam !== null && { cursor: pageParam }),
        },
      }),
      providesTags: ['Boards'],
    }),
    /**
     * Salva una posizione (`POST /boards`). ⚠️ Vive sul GAMESERVER (`env.qgammonWsUrl`): solo li' c'e' il registro delle partite
     * vive. Il corpo dice DOVE (`matchId`, `gameNumber`, `ply`, `withRoll`), mai la posizione. 201 la prima volta, 200 se quello
     * stato era gia' salvato: per noi sono lo stesso «salvato» (`creato` li distingue per chi lo vuole sapere).
     * 🔴 MAI ritentata da RTK (`maxRetries: 0`): e' una scrittura. Il ritentativo del `409 history_pending` e' NOSTRO e limitato
     * (`lib/myBoards/salva.ts`), non quello di `retry`.
     */
    captureBoard: builder.mutation<{ board: BoardView; creato: boolean }, CaptureBoardRequest>({
      query: (body) => ({ url: `${env.qgammonWsUrl}/boards`, method: 'POST', body }),
      transformResponse: (r: BoardResponse, meta) => ({ board: r.board, creato: meta?.response?.status === 201 }),
      extraOptions: NESSUN_RITENTATIVO,
      invalidatesTags: ['Boards'],
    }),
    /** Toglie un board dalla propria lista (`DELETE /boards/:id`, api): 204 anche se gia' tolto, 404 se non e' del chiamante. */
    deleteBoard: builder.mutation<void, string>({
      query: (id) => ({ url: `/boards/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: ['Boards'],
    }),
    /**
     * Una posizione (`GET /positions/:id`, api, PUBBLICA: nessun token): la posizione, la partita d'origine com'e' ADESSO, la
     * comunita' e lo scenario (`null` = non giocabile). 60 al minuto per chiamante ⇒ nessun polling.
     */
    getPosition: builder.query<PositionResponse, string>({
      query: (id) => `/positions/${encodeURIComponent(id)}`,
    }),
    /**
     * La stima del bot (`GET /positions/:id/analysis`, GAMESERVER, col Bearer, 20 al minuto per utente). Aperta solo a partita
     * d'origine finita e senza una partita viva sulla stessa posizione: i `409`/`422` sono chiusure (`lib/myBoards/errori.ts`).
     * Il `503` (`analysis_busy`, `analysis_timeout`) NON si ritenta da solo: il «Riprova» e' del giocatore, nessun ciclo.
     */
    getPositionAnalysis: builder.query<PositionAnalysisView, string>({
      query: (id) => `${env.qgammonWsUrl}/positions/${encodeURIComponent(id)}/analysis`,
      extraOptions: NESSUN_RITENTATIVO,
    }),
  }),
});

export const {
  useGetLeaderboardQuery,
  useGetMeQuery,
  useGetMyMatchesQuery,
  useGetReplayQuery,
  useGetMatchQuery,
  useGetMyStatsQuery,
  useGetMyEloHistoryQuery,
  useGetLiveMatchesQuery,
  useGetBoardsInfiniteQuery,
  useCaptureBoardMutation,
  useDeleteBoardMutation,
  useGetPositionQuery,
  useGetPositionAnalysisQuery,
} = qgammonApi;
